import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, ChevronRight, ChevronDown, Users as UsersIcon, UserCircle, Crown, Shield, Plus, Power, RotateCcw, Mail } from "lucide-react";

import { ROLES, TEAM_ROLES, DESIGNATIONS, DEPARTMENTS } from "./constants";

export function TableView({ users, isAdmin, updateUser, toggleActive, resetToDefault, emailCredentials }) {
  const editEmail = (u) => {
    const next = prompt(`Update login email for ${u.name}\n\nCurrent: ${u.email}\n\nThey will use the NEW email to sign in.`, u.email || "");
    if (next === null) return;
    const clean = next.trim().toLowerCase();
    if (!clean || !clean.includes("@")) { toast.error("Enter a valid email"); return; }
    if (clean === (u.email || "").toLowerCase()) return;
    updateUser(u.user_id, "email", clean);
  };
  const F = ({ label, children }) => (
    <div className="min-w-0">
      <div className="label-mono text-[8px] text-zinc-500">{label}</div>
      {children}
    </div>
  );
  const inputCls = "brut-input px-1.5 py-0.5 text-[11px] font-mono w-full";
  return (
    <div className="space-y-2" data-testid="users-compact-grid">
      {users.map((u) => {
        const isSuspended = u.is_active === false;
        return (
          <div key={u.user_id} className={"brut-card px-3 py-2 " + (isSuspended ? "opacity-60" : "")} data-testid={"user-row-" + u.user_id}>
            {/* Row 1 — identity */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <div className="w-7 h-7 bg-yellow-200 border border-zinc-900 flex items-center justify-center font-mono text-xs font-bold shrink-0">
                {u.name && u.name[0] ? u.name[0].toUpperCase() : "?"}
              </div>
              <span className="font-bold text-sm">{u.name}</span>
              <span className="inline-flex items-center gap-1">
                <span className="font-mono text-[11px] text-zinc-600">{u.email}</span>
                {isAdmin && (
                  <button onClick={() => editEmail(u)} className="brut-btn-secondary px-1 py-0 text-[9px] font-mono opacity-40 hover:opacity-100" title="Update login email" data-testid={`edit-email-${u.user_id}`}>✎</button>
                )}
              </span>
              <span className={"font-mono text-[9px] uppercase px-1.5 py-0.5 border border-zinc-900 " + (isSuspended ? "bg-red-200" : "bg-emerald-200")}>
                {isSuspended ? "Suspended" : "Active"}
              </span>
              <span className="font-mono text-[10px] text-zinc-500 ml-auto">Joined {u.created_at ? u.created_at.slice(0, 10) : "—"}</span>
            </div>
            {/* Row 2 — editable fields, auto-fitting columns */}
            <div className="grid gap-x-2 gap-y-1 mt-1.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))" }}>
              <F label="Emp ID">
                {isAdmin ? (
                  <input className={inputCls} defaultValue={u.employee_id || ""} onBlur={(e) => { if (e.target.value !== (u.employee_id || "")) updateUser(u.user_id, "employee_id", e.target.value); }} data-testid={"emp-id-" + u.user_id} />
                ) : <span className="font-mono text-xs">{u.employee_id || "-"}</span>}
              </F>
              <F label="Role">
                {isAdmin ? (
                  <select className={inputCls} value={u.role} onChange={(e) => updateUser(u.user_id, "role", e.target.value)} data-testid={"role-select-" + u.user_id}>
                    {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                ) : <span className="font-mono text-xs font-bold">{ROLE_LABELS[u.role]}</span>}
              </F>
              <F label="Team Role">
                {isAdmin ? (
                  <select className={inputCls} value={u.team_role || "employee"} onChange={(e) => updateUser(u.user_id, "team_role", e.target.value)} data-testid={"tree-team-role-" + u.user_id}>
                    {TEAM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : <span className="font-mono text-xs">{u.team_role || "employee"}</span>}
              </F>
              <F label="Designation">
                {isAdmin ? (
                  <select className={inputCls} value={u.designation || ""} onChange={(e) => updateUser(u.user_id, "designation", e.target.value || null)} data-testid={"designation-select-" + u.user_id}>
                    <option value="">— Unset —</option>
                    {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : <span className="font-mono text-xs">{u.designation || "—"}</span>}
              </F>
              <F label="Department">
                {isAdmin ? (
                  <select className={inputCls} value={u.department || ""} onChange={(e) => updateUser(u.user_id, "department", e.target.value || null)} data-testid={"department-select-" + u.user_id}>
                    <option value="">— Unset —</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                ) : <span className="font-mono text-xs">{u.department || "—"}</span>}
              </F>
              <F label="Manager">
                {isAdmin ? (
                  <select className={inputCls} value={u.manager_id || ""} onChange={(e) => updateUser(u.user_id, "manager_id", e.target.value || null)} data-testid={"tree-manager-" + u.user_id}>
                    <option value="">- None -</option>
                    {users.filter((other) => other.user_id !== u.user_id).map((other) => <option key={other.user_id} value={other.user_id}>{other.name}</option>)}
                  </select>
                ) : <span className="text-xs">{(users.find((m) => m.user_id === u.manager_id) || {}).name || "-"}</span>}
              </F>
              <F label="Team">
                {isAdmin ? (
                  <input className={inputCls} defaultValue={u.team || ""} onBlur={(e) => { if (e.target.value !== (u.team || "")) updateUser(u.user_id, "team", e.target.value); }} />
                ) : <span className="text-xs">{u.team || "-"}</span>}
              </F>
              <F label="Salary (INR)">
                {isAdmin ? (
                  <input type="number" className={inputCls + " text-right"} defaultValue={u.monthly_salary_inr || 0} onBlur={(e) => { const v = parseFloat(e.target.value) || 0; if (v !== (u.monthly_salary_inr || 0)) updateUser(u.user_id, "monthly_salary_inr", v); }} data-testid={"salary-" + u.user_id} />
                ) : <span className="font-mono text-xs">{(u.monthly_salary_inr || 0).toLocaleString("en-IN")}</span>}
              </F>
            </div>
            {/* Row 3 — actions */}
            {isAdmin && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                <button onClick={() => resetToDefault(u)} className="brut-btn-secondary px-1.5 py-0.5 text-[9px] font-mono flex items-center gap-0.5" data-testid={`reset-default-pw-${u.user_id}`} title="Reset password to onboarding default (LD@2026!)"><RotateCcw className="w-3 h-3" /> DEFAULT PW</button>
                <button onClick={() => emailCredentials(u)} className="brut-btn-secondary px-1.5 py-0.5 text-[9px] font-mono bg-sky-100 hover:bg-sky-200" data-testid={`email-creds-${u.user_id}`} title="Reset to a fresh random password and EMAIL this user their User ID + password">EMAIL CREDS</button>
                <button
                  onClick={async () => {
                    const pw = prompt(`Set a new password for ${u.name} (${u.email}).\n\nMin 8 characters. They will be forced to re-login.`);
                    if (pw === null) return;
                    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
                    try {
                      await api.post(`/admin/users/${u.user_id}/set-password`, { new_password: pw });
                      toast.success(`Password updated for ${u.email}`);
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed to set password"); }
                  }}
                  className="brut-btn-secondary px-1.5 py-0.5 text-[9px] font-mono" data-testid={`reset-pw-${u.user_id}`} title="Set a custom new login password for this user"
                >CUSTOM PW</button>
                <button onClick={() => toggleActive(u)} className={"brut-btn-secondary px-1.5 py-0.5 text-[9px] font-mono flex items-center gap-0.5 " + (isSuspended ? "bg-emerald-100 hover:bg-emerald-200" : "bg-amber-100 hover:bg-amber-200")} data-testid={`toggle-active-${u.user_id}`} title={isSuspended ? "Reactivate this account" : "Suspend this account"}><Power className="w-3 h-3" /> {isSuspended ? "ACTIVATE" : "SUSPEND"}</button>
                <button
                  onClick={async () => {
                    const confirmation = prompt(`HARD DELETE ${u.name} (${u.email})?\n\nThis irreversibly removes the user, unassigns their jobs, and clears them as a manager.\n\nType the email to confirm:`);
                    if (confirmation !== u.email) {
                      if (confirmation !== null) toast.error("Confirmation did not match — nothing deleted.");
                      return;
                    }
                    try {
                      const { data } = await api.delete(`/admin/users/${u.user_id}/hard-delete`);
                      toast.success(`Deleted ${data.deleted_email} · ${data.jobs_reassigned} jobs unassigned · ${data.managers_unset} managers cleared`);
                    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                  }}
                  className="brut-btn-secondary px-1.5 py-0.5 text-[9px] font-mono bg-red-100 hover:bg-red-200" data-testid={`hard-delete-${u.user_id}`} title="Permanently delete this user (jobs reassigned, sessions invalidated)"
                >DELETE</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Th({ children, align }) {
  return <th className={"text-" + (align || "left") + " px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap"}>{children}</th>;
}
export function Td({ children, align, className }) {
  return <td className={"text-" + (align || "left") + " px-3 py-2.5 " + (className || "")}>{children}</td>;
}

